import {
  AssetTypes,
  getAssetRatio,
  getAnimatedThumbnailResource,
} from '@hooks/studioContext/CMSParams';

export const ANIMATED_THUMBNAIL_DURATION_SECONDS = 3;
export const ANIMATED_THUMBNAIL_ASPECT_RATIO = 2 / 3;
const PROCESSING_PLACEHOLDER_PATH = 'assets/_system/processing';

export const isPlayableMp4 = (url) => {
  if (typeof url !== 'string' || !url.trim()) {
    return false;
  }

  const path = url.split(/[?#]/, 1)[0];
  return (
    !path.toLowerCase().includes(PROCESSING_PLACEHOLDER_PATH) &&
    path.toLowerCase().endsWith('.mp4')
  );
};

const isVideoType = (type) =>
  typeof type === 'string' && type.toLowerCase() === 'video';

const isAbsoluteHttpUrl = (url) =>
  typeof url === 'string' && /^https?:\/\//i.test(url.trim());

const getPlayableStorySourceUrl = (page) => {
  if (isPlayableMp4(page?.assetUrl)) {
    return page.assetUrl;
  }

  if (
    isAbsoluteHttpUrl(page?.assetVideoUrl) &&
    isPlayableMp4(page.assetVideoUrl)
  ) {
    return page.assetVideoUrl;
  }

  return null;
};

const editableSourceLabels = {
  manualUpload: 'Uploaded Video Thumbnail source',
  clipVideo: 'Clip video',
  storyFirstPage: 'Story first page',
};

export const resolveAnimatedThumbnailEditableSource = (animatedThumbnail) => {
  const editableSource = animatedThumbnail?.editableSource;
  const sourceId = editableSource?.sourceId;
  const playbackUrl = editableSource?.playbackUrl;
  const startSeconds = Number(editableSource?.startSeconds);

  if (
    typeof sourceId !== 'string' ||
    !sourceId.trim() ||
    !isPlayableMp4(playbackUrl)
  ) {
    return null;
  }

  return {
    label:
      editableSourceLabels[editableSource.kind] || 'Video Thumbnail source',
    sourceId,
    sourceKind: editableSource.kind,
    startSeconds:
      Number.isFinite(startSeconds) && startSeconds >= 0 ? startSeconds : 0,
    url: playbackUrl,
  };
};

export const resolveAnimatedThumbnailSource = (assetType, payload) => {
  if (assetType === AssetTypes.ClipAnimatedThumbnail) {
    const posterUrl = payload?.videoThumbnailUrl || payload?.customThumbnailUrl;

    return isPlayableMp4(payload?.videoUrl)
      ? {
          url: payload.videoUrl,
          ...(posterUrl ? { posterUrl } : {}),
          label: payload.internalTitle || payload.displayTitle || 'Clip video',
        }
      : null;
  }

  if (
    assetType === AssetTypes.StoryAnimatedThumbnail ||
    assetType === AssetTypes.StoryCircularAnimatedThumbnail
  ) {
    const firstPage = [...(payload?.pages || [])].sort(
      (left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0)
    )[0];
    const sourceUrl = getPlayableStorySourceUrl(firstPage);
    const posterUrl = firstPage?.playcardUrl || firstPage?.playcardImageUrl;

    return firstPage && isVideoType(firstPage.type) && isPlayableMp4(sourceUrl)
      ? {
          url: sourceUrl,
          ...(posterUrl ? { posterUrl } : {}),
          label:
            payload?.titles?.internal || payload?.title || 'Story first page',
        }
      : null;
  }

  return null;
};

export const isAnimatedThumbnailProcessing = (animatedThumbnail) =>
  animatedThumbnail?.resource?.status?.toLowerCase() === 'processing';

export const getAnimatedThumbnailMaxStart = (duration) =>
  Number.isFinite(duration) && duration >= ANIMATED_THUMBNAIL_DURATION_SECONDS
    ? Math.max(0, duration - ANIMATED_THUMBNAIL_DURATION_SECONDS)
    : 0;

export const clampAnimatedThumbnailStart = (startSeconds, duration) => {
  const start = Number(startSeconds);
  if (!Number.isFinite(start)) {
    return 0;
  }

  return Math.min(Math.max(0, start), getAnimatedThumbnailMaxStart(duration));
};

export const getAnimatedThumbnailSaveDisabledReason = ({
  duration,
  hasLoadError,
  isLoading,
  isProcessing,
  isSaving,
  source,
}) => {
  if (isLoading) {
    return 'Loading the source video.';
  }
  if (hasLoadError) {
    return 'The source or animated-thumbnail state could not be loaded.';
  }
  if (isSaving) {
    return 'Saving the selected segment.';
  }
  if (isProcessing) {
    return 'Generation is blocked while the animated thumbnail is processing.';
  }
  if (!source) {
    return 'The current media does not have a playable MP4 source.';
  }
  if (!Number.isFinite(duration) || duration <= 0) {
    return 'Waiting for the source video duration.';
  }
  if (duration < ANIMATED_THUMBNAIL_DURATION_SECONDS) {
    return 'Videos shorter than three seconds are unsupported.';
  }

  return null;
};

export const getAnimatedThumbnailSourceApi = (cmsApi, assetType) => {
  if (
    assetType === AssetTypes.StoryAnimatedThumbnail ||
    assetType === AssetTypes.StoryCircularAnimatedThumbnail
  ) {
    return cmsApi.storyApi;
  }
  if (assetType === AssetTypes.ClipAnimatedThumbnail) {
    return cmsApi.clipApi;
  }
  return null;
};

export const getAnimatedThumbnailShape = (assetType) =>
  assetType === AssetTypes.StoryCircularAnimatedThumbnail
    ? 'circular'
    : undefined;

export const getAnimatedThumbnailAspectRatio = (assetType) => {
  const aspectRatio = getAssetRatio(assetType);

  return getAnimatedThumbnailResource(assetType) && aspectRatio > 0
    ? aspectRatio
    : ANIMATED_THUMBNAIL_ASPECT_RATIO;
};

export { getAnimatedThumbnailResource };
